varying vec2 vUv; 
uniform float u_time;
uniform float u_smooth;
uniform float u_size;
uniform vec2 u_resolution;
uniform float u_pixelRatio;
uniform float u_lightspeed;
uniform float u_hardness;
uniform float u_noiseFrequency;
#define PI 3.14159265358979;
#define STEPS 25
#define LIGHT_SPEED u_lightspeed
#define HARDNESS u_hardness
//	Classic Perlin 3D Noise 
//	by Stefan Gustavson (https://github.com/stegu/webgl-noise)
//
vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
vec3 fade(vec3 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}

float cnoise(vec3 P){
  vec3 Pi0 = floor(P); // Integer part for indexing
  vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
  Pi0 = mod(Pi0, 289.0);
  Pi1 = mod(Pi1, 289.0);
  vec3 Pf0 = fract(P); // Fractional part for interpolation
  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 / 7.0;
  vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 / 7.0;
  vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x;
  g010 *= norm0.y;
  g100 *= norm0.z;
  g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x;
  g011 *= norm1.y;
  g101 *= norm1.z;
  g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
  return 2.2 * n_xyz;
}
struct ray {
	vec2 t;
	vec2 p;
	vec2 d;
};

/**
SCENE
*/
vec2 rotateCW(vec2 p, float a)
{
	mat2 m = mat2(cos(a), -sin(a), sin(a), cos(a));
	return p * m;
}

float circleDist(vec2 p, float radius)
{
	return length(p) - radius;
}

vec3 sdgBox( in vec2 p, in vec2 b, float ra )
{
    vec2 w = abs(p)-(b-ra);
    vec2 s = vec2(p.x<0.0?-1:1,p.y<0.0?-1:1);
    
    float g = max(w.x,w.y);
	vec2  q = max(w,0.0);
    float l = length(q);
    
    return vec3(   (g>0.0)?l-ra: g-ra,
                s*((g>0.0)?q/l : ((w.x>w.y)?vec2(1,0):vec2(0,1))));
}
float sceneDist(vec2 p){


    vec2 circlePos = vec2(400., 400.);
    float circle = circleDist(p-circlePos, u_size);
    float finalDist = circle;

    vec2 circle2Pos = vec2(-400., -400.);
    float circle2 = circleDist(p-circle2Pos, u_size);
    finalDist = min(circle, circle2);

    vec2 circle3Pos = vec2(400., -400.);
    float circle3 = circleDist(p-circle3Pos, u_size);
    finalDist = min(finalDist, circle3);

    vec2 circle4Pos = vec2(-400., 400.);
    float circle4 = circleDist(p-circle4Pos, u_size);
    finalDist = min(finalDist, circle4);

    vec2 circle5Pos = vec2(-200., 200.);
    float circle5 = circleDist(p-circle5Pos, u_size*0.5);
    finalDist = min(finalDist, circle5);
    vec2 circle6Pos = vec2(-200., -200.);
    float circle6 = circleDist(p-circle6Pos, u_size*0.5);
    finalDist = min(finalDist, circle6);
    vec2 circle7Pos = vec2(200., -200.);
    float circle7 = circleDist(p-circle7Pos, u_size*0.5);
    finalDist = min(finalDist, circle7);
    vec2 circle8Pos = vec2(200., 200.);
    float circle8 = circleDist(p-circle8Pos, u_size*0.5);
    finalDist = min(finalDist, circle8);

    return 1.-finalDist;
}
/**
rays
*/
ray newRay (in vec2 origin, in vec2 target) {
	ray r;
	
	r.t = target;
	r.p = origin;
	r.d = (target - origin) / float (STEPS);
	
	return r;
}

void rayMarch (inout ray r) {
	r.p += r.d * clamp (HARDNESS - sceneDist (r.p) * HARDNESS * 2.0, 0.0, LIGHT_SPEED);
}

vec3 light (in ray r, in vec3 color) {
	return color / (dot (r.p, r.p) + color);
}


void main()
{
    vec4 finalCol = vec4(0.1);

    vec2 ndc =( (vUv *2.)-1.) * u_resolution;
    ndc.y = -ndc.y;
    ndc*=u_pixelRatio;
// vec2 testUv =  rotateCW(vUv-0.5, u_time);
    float sceneDistance = clamp(0., 1., sceneDist(ndc));
    
    float offset1 = 50.*abs(cos(u_time)*0.01);
    offset1 = smoothstep(0.2, 1., offset1);


    // PRESET1
    // float light0Y = -(mod(u_time, 1.)*10000.)+5000.;
    // vec2 light0Pos = vec2 (0., light0Y);
    // float light1Y = (mod(u_time+0.5, 1.)*10000.)-5000.;
    // vec2 light1Pos = vec2 (0., light1Y);
    // float light2X = (mod(u_time+0.25, 1.)*10000.)-5000.;
    // vec2 light2Pos = vec2 (light2X, 0.);
    // float light3X = -(mod(u_time+0.75, 1.)*10000.)+5000.;
    // vec2 light3Pos = vec2 (light3X, 0.);


    // PRESET2
    float light0Y = -(mod(u_time, 1.)*10000.)+5000.;
    vec2 light0Pos = vec2 (0., light0Y);
    float light1Y = (mod(u_time, 1.5)*10000.)-5000.;
    vec2 light1Pos = vec2 (0., light1Y);
    float light2X = (mod(u_time, 1.5)*10000.)-5000.;
    vec2 light2Pos = vec2 (light2X, 0.);
    float light3X = -(mod(u_time, 2.)*10000.)+5000.;
    vec2 light3Pos = vec2 (light3X, 0.);



    	ray r0 = newRay (ndc, light0Pos);
    	ray r1 = newRay (ndc, light1Pos);
    	ray r2 = newRay (ndc, light2Pos);
    	ray r3 = newRay (ndc, light3Pos);

	for (int i = 0; i < STEPS; i++) {
		rayMarch (r0);
		rayMarch (r1);
		rayMarch (r2);
		rayMarch (r3);

	}
	
	r0.p -= r0.t;
	r1.p -= r1.t;
	r2.p -= r2.t;
	r3.p -= r3.t;


  	vec3 light0 = light (r0, vec3 (0.3, 0.2, 0.1) * 80000.0);
  	vec3 light1 = light (r1, vec3 (0.1, 0.2, 0.3) * 80000.0);
  	vec3 light2 = light (r2, vec3 (0.3, 0.2, 0.2) * 80000.0);
  	vec3 light3 = light (r3, vec3 (0.2, 0.2, 0.3) * 80000.0);
 

    // finalCol = finalCol + sceneDistance;
    float f = clamp (sceneDistance * 200.0 - 100.0, 0.0, 3.0);

    vec3 finalLight = light0 + light1+light2+light3;
    // vec3 finalLight = light2+light3;





    gl_FragColor = vec4 ((finalLight) * (1.0 + f), 1.0);
    // gl_FragColor = vec4(vec2(ndc*10.), 0., 1.);
    // gl_FragColor = vec4(vec3(sceneDistance), 1.);
}